import { createMemo, Show, type Accessor, type JSX } from 'solid-js';
import { MusicRateControl } from './MusicRateControl';
import { Switch } from './Switch';
import { effectSyncIndicator, type EffectClockMirror } from '../lib/effectSync';
import type { MusicRate } from '../lib/musicRate';
import { useVenue } from '../state/VenueProvider';
import './EffectSyncControl.css';
import { testIdOf } from '../testIds';

/** What the controller reports about a running effect's clock, and what the room remembered before it ran. */
export interface EffectSyncSource {
  on: boolean;
  musicRate?: string;
}

export interface EffectSyncModel {
  enabled: Accessor<boolean>;
  musicRate: Accessor<string>;
  /** The room has a music input committed. */
  hasInput: Accessor<boolean>;
  /** The room is LISTENING to it — the one fact this switch depends on. */
  listening: Accessor<boolean>;
  status: Accessor<EffectClockMirror | undefined>;
  setEnabled: (value: boolean) => void;
  setMusicRate: (rate: MusicRate) => void;
}

interface EffectSyncOptions {
  /** The running effect this switch drives. Empty while nothing is running, which is still configurable. */
  effectId: Accessor<string>;
  /** The controller's own clock readout for that effect. */
  reported: Accessor<(EffectClockMirror & { musicRate?: string }) | undefined>;
  /** What this room last used for this effect, so the switch opens where the operator left it. */
  remembered: Accessor<EffectSyncSource | undefined>;
  /** A compound host can keep the shared surface while carrying sync inside its own patch. */
  onChange: (field: 'enabled' | 'musicRate', value: boolean | MusicRate) => void;
}

/**
 * The shared sync surface: "Sync to music" and the six-rate selector every layer uses.
 *
 * It is one model per open card or layer. The host supplies the write path through onChange, while the
 * sync fields remain separate from the effect's numeric speed parameter.
 */
export function createEffectSyncModel(options: EffectSyncOptions): EffectSyncModel {
  const venue = useVenue();
  const enabled = createMemo(() => options.reported()?.on ?? options.remembered()?.on ?? false);
  const musicRate = createMemo<string>(() => {
    const value = options.reported()?.musicRate ?? options.remembered()?.musicRate;
    return typeof value === 'string' && value.length > 0 ? value : 'bar';
  });
  const hasInput = createMemo(() => Boolean(venue.roomMusicInput()));
  const listening = createMemo(() => venue.roomMusicListening());
  const status = createMemo<EffectClockMirror | undefined>(() => options.reported());
  return {
    enabled, musicRate, hasInput, listening, status,
    setEnabled: (value) => {
      if (value && !listening()) return;
      options.onChange('enabled', value);
    },
    setMusicRate: (value) => {
      if (value !== musicRate()) options.onChange('musicRate', value);
    },
  };
}

export function EffectSyncControl(props: {
  model: EffectSyncModel;
  /** The package's own sentence about what being locked to the music does to it. */
  description: string;
  idPrefix: string;
  /** Compound hosts can supply their own already-computed control gate. */
  controlsEnabled?: Accessor<boolean>;
  /** A saved look has no live clock status to report while it is being authored. */
  showStatus?: boolean;
  rates?: readonly MusicRate[];
}): JSX.Element {
  const venue = props.controlsEnabled ? undefined : useVenue();
  const controlsEnabled = (): boolean => props.controlsEnabled?.() ?? venue?.controlsEnabled() ?? false;
  /**
   * The switch shows the EFFECTIVE state, not the remembered one. An effect keeps its preference on the
   * controller, so an effect last used with music on came back showing "Sync to music" checked in a room
   * whose ear is switched off — the console asserting a thing that is not happening. The preference is
   * not lost: it is what the switch returns to the moment the room listens again.
   */
  const effective = createMemo(() => props.model.enabled() && props.model.listening());
  const indicator = createMemo(() => effectSyncIndicator(
    effective(),
    props.model.hasInput(),
    props.model.status(),
  ));

  return (
    <div class="effect-sync-control" data-testid={testIdOf('syncControl', { prefix: props.idPrefix })}>
      <div class="effect-sync-copy">
        <strong>Sync to music</strong>
        <Show when={props.description}><small>{props.description}</small></Show>
      </div>
      <Switch
        checked={effective()}
        disabled={!controlsEnabled() || !props.model.listening()}
        label="Sync to music"
        testId={testIdOf('sync', { prefix: props.idPrefix })}
        onChange={(checked) => props.model.setEnabled(checked)}
      />
      <Show when={props.showStatus !== false && props.model.listening()}>
        <Show when={indicator()}>
          {(message) => (
            <span
              class="effect-sync-indicator"
              classList={{ locked: Boolean(props.model.status()?.locked), warning: message() === 'No music input' }}
              data-testid={testIdOf('syncStatus', { prefix: props.idPrefix })}
            >{message()}</span>
          )}
        </Show>
      </Show>
      <Show when={!props.model.hasInput()}>
        <a class="effect-sync-input-link" href="#/music">Pick a music input first</a>
      </Show>
      <Show when={effective()}>
        <MusicRateControl
          value={props.model.musicRate()}
          onChange={(rate) => props.model.setMusicRate(rate)}
          disabled={!controlsEnabled()}
          idPrefix={props.idPrefix}
          rates={props.rates}
        />
      </Show>
    </div>
  );
}
